'use client';

import React from 'react';
import { addDays, format, startOfDay, startOfWeek } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@clerk/nextjs';
import { FaArrowRight } from 'react-icons/fa6';
import { FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { trpc } from '@/app/api/trpc/client';
import { CalendarWeekSummary } from '@/app/calendar/CalendarWeekSummary';
import Loading from '@/app/components/Loading';
import { CalendarDayActivity } from '@/app/calendar/day/CalendarDayActivities';
import { CalendarDayActivityModal } from '@/app/calendar/day/CalendarDayActivitiesModal';
import { CalendarDayWorkout } from '@/app/calendar/day/CalendarDayWorkouts';
import { EditWorkoutModal } from '@/app/calendar/day/EditWorkoutModal';
import { WeeklyZonesChart } from '@/app/components/WeeklyZonesChart';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import type {
  ActivityWithRelations,
  WorkoutWithRelations,
} from '@/utils/types';
import { cn } from '@/utils/uiUtils';

const cardClass =
  'rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900';
const labelClass =
  'text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400';

export default function HomeDashboard() {
  const { isLoaded, isSignedIn } = useAuth();
  const isUserReady = isLoaded && isSignedIn;
  const weekStart = startOfWeek(startOfDay(new Date()), { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, 7);
  const today = startOfDay(new Date());
  const { showChatWindow, setShowChatWindow } = useCalendarState();
  const utils = trpc.useUtils();
  const { mutate: syncStrava, isPending: isSyncingStrava } =
    trpc.strava.syncStrava.useMutation({
      onSuccess: (data) => {
        const count = data?.count ?? 0;
        toast.success(
          `Synced ${count} Strava ${count === 1 ? 'activity' : 'activities'}.`
        );
        utils.activities.getMany.invalidate();
      },
      onError: (error) => {
        toast.error('Strava sync failed.');
        console.error(error);
      },
    });
  const activitiesQuery = trpc.activities.getMany.useQuery({
    limit: 200,
    filter: { startDate: { gte: weekStart, lt: weekEnd } },
  });
  const workoutsQuery = trpc.workouts.getMany.useQuery({
    limit: 50,
    filter: { date: { gte: weekStart, lt: weekEnd } },
  });
  const physiologyQuery = trpc.physiology.latest.useQuery(undefined, {
    enabled: isUserReady,
  });
  const dailiesQuery = trpc.garmin.getDailies.useQuery(undefined, {
    enabled: isUserReady,
  });
  const calendarDate = {
    gte: format(today, 'yyyy-MM-dd'),
    lt: format(addDays(today, 1), 'yyyy-MM-dd'),
  };
  const todayPhysiologyQuery = trpc.physiology.getMany.useQuery(
    {
      filter: { calendarDate },
      limit: 1,
    },
    { enabled: isUserReady }
  );
  const todayDailiesQuery = trpc.garmin.getManyDailies.useQuery(
    {
      filter: { calendarDate },
      limit: 1,
    },
    { enabled: isUserReady }
  );
  const headlineQuery = trpc.analysis.getHeadline.useQuery({
    date: today,
    dayOffset: 0,
  });
  const trainingSettingsQuery = trpc.settings.getTrainingSettings.useQuery();
  const activities = activitiesQuery.data?.activities ?? [];
  const workouts = workoutsQuery.data?.workouts ?? [];
  const todayWorkouts = workouts.filter(
    (workout) =>
      workout.date &&
      startOfDay(new Date(workout.date)).getTime() === today.getTime()
  );
  const todayActivities = activities.filter(
    (activity) =>
      activity.startDate &&
      startOfDay(new Date(activity.startDate)).getTime() === today.getTime()
  );
  const physiology = todayPhysiologyQuery.data ?? physiologyQuery.data;
  const daily = todayDailiesQuery.data ?? dailiesQuery.data;
  const coachProfile = trainingSettingsQuery.data?.coachProfile;

  return (
    <div className='dashboard-home min-h-full bg-slate-50 px-3 py-5 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:px-6 lg:px-10'>
      <div className='mx-auto max-w-7xl space-y-4'>
        <div className='flex flex-wrap items-end justify-between gap-3 px-1'>
          <div>
            <p className={labelClass}>Dashboard</p>
            <h1 className='mt-1 text-3xl font-bold tracking-tight'>
              Your training week
            </h1>
          </div>
          <p className='text-sm font-medium text-slate-500 dark:text-slate-400'>
            {format(weekStart, 'MMM d')} -{' '}
            {format(addDays(weekStart, 6), 'MMM d')}
          </p>
        </div>
        <section
          className={cn(cardClass, 'p-3 sm:p-5')}
          aria-labelledby='weekly-progress'
        >
          <div className='mb-3 flex items-center justify-between'>
            <h2 id='weekly-progress' className='text-xl font-semibold'>
              Weekly Progress
            </h2>
            <Link
              href='/calendar'
              className='text-sm font-semibold text-emerald-600 hover:underline dark:text-emerald-400'
            >
              Open calendar <FaArrowRight className='ml-1 inline' />
            </Link>
          </div>
          <CalendarWeekSummary
            week={{ start: weekStart, end: weekEnd, index: 0 }}
            activities={activities as ActivityWithRelations[]}
            workouts={workouts as WorkoutWithRelations[]}
            variant='dashboard'
          />
        </section>
        <section
          className={cn(cardClass, 'p-4 sm:p-5')}
          aria-labelledby='weekly-zones'
        >
          <h2 id='weekly-zones' className='mb-4 text-xl font-semibold'>
            Weekly In Zones
          </h2>
          <WeeklyZonesChart
            activities={activities as ActivityWithRelations[]}
            workouts={workouts as WorkoutWithRelations[]}
            weekStart={weekStart}
            isUserReady={isUserReady}
          />
        </section>
        <section
          className={cn(cardClass, 'p-4 sm:p-5')}
          aria-labelledby='physiology'
        >
          <div className='flex items-center justify-between'>
            <h2 id='physiology' className={labelClass}>
              Physiology <span className='ml-2 tracking-normal'>Garmin</span>
            </h2>
            <span className='text-sm font-semibold text-emerald-600 dark:text-emerald-400'>
              {format(today, 'EEE MM/dd')}
            </span>
          </div>
          <div className='mt-5 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6'>
            <Metric
              label='Rest HR'
              value={
                typeof daily?.restingHeartRate === 'number'
                  ? `${Math.round(daily.restingHeartRate)} bpm`
                  : '—'
              }
            />
            <Metric
              label='Steps'
              value={
                typeof daily?.steps === 'number'
                  ? daily.steps.toLocaleString()
                  : '—'
              }
            />
            <Metric
              label='Stress'
              value={
                typeof physiology?.stressAverage === 'number'
                  ? `${Math.round(physiology.stressAverage)}`
                  : '—'
              }
            />
            <Metric
              label='Battery'
              value={
                typeof physiology?.bodyBatteryCharged === 'number'
                  ? `${Math.round(physiology.bodyBatteryCharged)}`
                  : '—'
              }
            />
            <Metric
              label='Sleep Hours'
              value={
                typeof physiology?.sleepDuration === 'number'
                  ? `${(physiology.sleepDuration / 3600).toFixed(1)}h`
                  : '—'
              }
            />
            <Metric
              label='Sleep Score'
              value={
                typeof physiology?.sleepQuality === 'number'
                  ? `${Math.round(physiology.sleepQuality)}`
                  : '—'
              }
            />
          </div>
        </section>
        <section
          className={cn(cardClass, 'p-4 sm:p-5')}
          aria-labelledby='coach-headline'
        >
          <div
            role='button'
            tabIndex={0}
            className='cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400'
            aria-label='Open coach chat'
            onClick={() => setShowChatWindow(!showChatWindow)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowChatWindow(!showChatWindow);
              }
            }}
          >
            <div className='flex items-center gap-2'>
              {coachProfile?.photoUrl || coachProfile?.avatarUrl ? (
                <Image
                  src={coachProfile.photoUrl ?? coachProfile.avatarUrl ?? ''}
                  alt='Coach avatar'
                  width={28}
                  height={28}
                  className='h-7 w-7 rounded-full object-cover'
                />
              ) : (
                <div className='flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-xs font-semibold text-white'>
                  {(coachProfile?.name ?? 'C').charAt(0).toUpperCase()}
                </div>
              )}
              <h2 id='coach-headline' className={labelClass}>
                Coach Insights
              </h2>
            </div>
            {headlineQuery.isLoading ? (
              <Loading className='my-4' />
            ) : (
              <p className='mt-3 line-clamp-3 text-base leading-relaxed text-slate-800 dark:text-slate-100'>
                {headlineQuery.data?.text ?? 'No daily headline yet.'}
              </p>
            )}
          </div>
        </section>
        <section
          className={cn(cardClass, 'p-4 sm:p-5')}
          aria-label='Workout and activity'
        >
          <div className='grid items-start gap-6 lg:grid-cols-2'>
            <div aria-labelledby='workout' className='order-2 flex flex-col'>
              <div className='flex h-6 items-center'>
                <h2 id='workout' className={labelClass}>
                  Workout
                </h2>
              </div>
              {workoutsQuery.isLoading ? (
                <Loading className='my-8' />
              ) : todayWorkouts.length === 0 ? (
                <div className='mt-4 flex items-center justify-center rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400'>
                  No workout scheduled for today. Check your plan to add a
                  session.
                </div>
              ) : (
                <div className='mt-4 grid grid-cols-2 gap-3'>
                  {todayWorkouts.map((workout) => (
                    <HomeWorkoutCard
                      key={workout.id}
                      workout={workout as WorkoutWithRelations}
                    />
                  ))}
                </div>
              )}
            </div>
            <div aria-labelledby='activity' className='order-1 flex flex-col'>
              <div className='flex h-6 items-center justify-between'>
                <h2 id='activity' className={labelClass}>
                  Activities
                </h2>
                <button
                  type='button'
                  className={cn(
                    'inline-flex items-center justify-center rounded-full p-1 transition-opacity',
                    isSyncingStrava && 'cursor-wait opacity-80'
                  )}
                  disabled={isSyncingStrava}
                  onClick={() => syncStrava({ fromDaysAgo: 1 })}
                  title="Sync today's Strava data"
                >
                  <FiRefreshCw
                    className={cn(
                      'text-emerald-500',
                      isSyncingStrava && 'animate-spin'
                    )}
                  />
                  <span className='sr-only'>Sync today&apos;s Strava data</span>
                </button>
              </div>
              {activitiesQuery.isLoading ? (
                <Loading className='my-8' />
              ) : todayActivities.length === 0 ? (
                <div className='mt-4 flex items-center justify-center p-8 text-center text-slate-500 dark:text-slate-400'>
                  No activity recorded today.
                </div>
              ) : (
                <div className='mt-4 grid grid-cols-2 gap-3'>
                  {todayActivities.map((activity) => (
                    <HomeActivityCard
                      key={activity.id}
                      activity={activity as ActivityWithRelations}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className='text-xs uppercase text-slate-500 dark:text-slate-400'>
        {label}
      </p>
      <p className='mt-1 text-lg font-semibold'>{value}</p>
    </div>
  );
}

function HomeWorkoutCard({ workout }: { workout: WorkoutWithRelations }) {
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const { setWorkout } = useCalendarState();

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        onClick={() => {
          setWorkout(workout);
          setIsEditorOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setWorkout(workout);
            setIsEditorOpen(true);
          }
        }}
        className='dashboard-card flex w-full cursor-pointer overflow-hidden rounded-lg border-l-4 border-amber-400 text-left transition hover:-translate-y-0.5 hover:shadow-md'
      >
        <CalendarDayWorkout date={new Date()} workout={workout} active />
      </div>
      <EditWorkoutModal
        isModalOpen={isEditorOpen}
        close={() => setIsEditorOpen(false)}
      />
    </>
  );
}

function HomeActivityCard({ activity }: { activity: ActivityWithRelations }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        onClick={() => setIsModalOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsModalOpen(true);
          }
        }}
        className='dashboard-card flex w-full cursor-pointer overflow-hidden rounded-lg border-l-4 border-emerald-400 text-left transition hover:-translate-y-0.5 hover:shadow-md'
      >
        <CalendarDayActivity activity={activity} />
      </div>
      <CalendarDayActivityModal
        activity={activity}
        isModalOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
      />
    </>
  );
}
